/**
 * State preservation for the processing pipeline.
 *
 * When the editor dispatches a change, a block's text changes. We re-tokenise
 * the new text from scratch (giving every token/sentence editState: new) and
 * then restore spelling state for any part of the text that was NOT touched by
 * the edit.
 *
 * Strategy — character-range-based, no heuristics:
 *
 *   1. Compute the precise dirty region using longest-common-prefix /
 *      longest-common-suffix on the two text strings.  This gives
 *      [changeStart, changeEnd) in the prior text and [changeStart, newEnd)
 *      in the new text.  Everything outside those ranges is definitionally
 *      unchanged.
 *
 *   2. Sentence-level classification (paragraph-local char offsets):
 *      - Sentence ends ≤ changeStart     → entirely before the edit; restore
 *        wholesale by matching the old sentence at the same paragraph-local
 *        start position (text-verified).
 *      - Sentence starts ≥ newEnd        → entirely after the edit (shifted
 *        by delta); restore wholesale by matching old position = new − delta.
 *      - Sentence overlaps dirty region  → apply token-level preservation
 *        using the old sentence at the same paragraph-local start.  If no
 *        such old sentence exists (e.g. a split produced a brand-new sentence
 *        mid-paragraph) it stays fully new — no heuristics.
 *
 *   3. Token-level preservation inside a matched dirty sentence pair:
 *      Scan left for unchanged token text (prefix) → restore state.
 *      Scan right for unchanged token text (suffix) → restore state.
 *      Remaining middle tokens keep editState: new.
 *
 *   4. editState propagates bottom-up: new > local > checked.
 *      A sentence/paragraph whose tokens are all local-or-checked is local,
 *      not checked — the old code incorrectly set it to checked, causing the
 *      pipeline to skip server lookup for paragraphs that still need it.
 *
 * Pipeline-internal — not part of the shared contract between editors and pipeline.
 */

import { editStateEnum, maxEditState, paragraphDTO, sentenceDTO, tokenDTO } from '@capiche/ts-data-models-shared';
import { CapicheLoggerCore, LogLevel } from '@capiche/ts-logger-core';
// ─────────────────────────────────────────────────────────────────────────────
// Change-range computation
// ─────────────────────────────────────────────────────────────────────────────

const logger: CapicheLoggerCore = new CapicheLoggerCore('preserve-paragraph-state:', LogLevel.error, true);
/**
 * Character offsets of the minimal dirty region between two text strings.
 *
 * Everything in [0, changeStart) is identical in both strings.
 * Everything in [changeEnd, prior.length) / [newEnd, post.length) is identical.
 * The dirty region in prior is [changeStart, changeEnd).
 * The dirty region in post  is [changeStart, newEnd).
 *
 * delta = post.length - prior.length = newEnd - changeEnd.
 */
export interface ChangeRange {
  changeStart: number;
  changeEnd: number;
  newEnd: number;
}

/**
 * Compute the minimal dirty character region between two strings using
 * longest-common-prefix (LCP) and longest-common-suffix (LCS).
 *
 * Returns { changeStart, changeEnd, newEnd } where:
 *   [0, changeStart)              — identical in both strings (unchanged prefix)
 *   [changeStart, changeEnd)      — dirty region in prior
 *   [changeStart, newEnd)         — dirty region in post
 *   [changeEnd, prior.length)     — identical in both strings (unchanged suffix)
 *
 * Special cases:
 *   Identical strings → changeStart = changeEnd = newEnd = string length.
 *                        Dirty region is empty; all content is unchanged.
 *   Pure append       → changeStart = changeEnd = prior.length; newEnd = post.length.
 *   Pure prepend      → changeStart = 0; changeEnd = 0; newEnd = inserted length.
 *   Full replacement  → changeStart = 0; changeEnd = prior.length; newEnd = post.length.
 */
export function computeChangeRange(prior: string, post: string): ChangeRange {
  const pl = prior.length;
  const nl = post.length;

  // Find the longest common prefix.
  let changeStart = 0;
  while (changeStart < pl && changeStart < nl && prior[changeStart] === post[changeStart]) {
    changeStart++;
  }

  // Find the longest common suffix without overlapping the prefix.
  let priorTail = pl;
  let postTail = nl;
  while (priorTail > changeStart && postTail > changeStart && prior[priorTail - 1] === post[postTail - 1]) {
    priorTail--;
    postTail--;
  }

  return { changeStart, changeEnd: priorTail, newEnd: postTail };
}

// ─────────────────────────────────────────────────────────────────────────────
// editState derivation (bottom-up: new > local > checked)
// ─────────────────────────────────────────────────────────────────────────────

export function deriveEditStateFromTokens(tokens: tokenDTO[]): editStateEnum {
  if (tokens.some((t) => t.editState === editStateEnum.new)) return editStateEnum.new;
  if (tokens.some((t) => t.editState === editStateEnum.local)) return editStateEnum.local;
  return editStateEnum.checked;
}

export function deriveEditStateFromSentences(sentences: sentenceDTO[]): editStateEnum {
  if (sentences.some((s) => s.editState === editStateEnum.new)) return editStateEnum.new;
  if (sentences.some((s) => s.editState === editStateEnum.local)) return editStateEnum.local;
  return editStateEnum.checked;
}

// ─────────────────────────────────────────────────────────────────────────────
// Token level
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Copy spellingState / editState / suggestions from `old` into matching tokens
 * in `next`. "Matching" means identical `.input` text, scanning inward from
 * both ends (prefix then suffix).
 *
 * Tokens in the unchanged prefix/suffix get their old state restored.
 * Tokens in the changed middle keep editState: new.
 *
 * Pure function — returns a new array, does not mutate either input.
 */
export function preserveTokenState(old: tokenDTO[], next: tokenDTO[]): tokenDTO[] {
  const result: tokenDTO[] = next.map((t) => ({ ...t }));

  const oLen = old.length;
  const nLen = next.length;

  // Scan left — find unchanged prefix.
  let left = 0;
  while (left < oLen && left < nLen && old[left].input === next[left].input) {
    result[left] = {
      ...result[left],
      editState: old[left].editState,
      spellingState: old[left].spellingState,
      suggestions: old[left].suggestions,
    };
    left++;
  }

  // Scan right — find unchanged suffix (must not overlap the prefix).
  let oRight = oLen - 1;
  let nRight = nLen - 1;
  while (oRight >= left && nRight >= left && old[oRight].input === next[nRight].input) {
    result[nRight] = {
      ...result[nRight],
      editState: old[oRight].editState,
      spellingState: old[oRight].spellingState,
      suggestions: old[oRight].suggestions,
    };
    oRight--;
    nRight--;
  }

  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sentence level
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Restore sentence state after a paragraph edit using the computed change range.
 *
 * The LCP/LCS character-range calculation is the oracle: everything outside
 * [changeStart, changeEnd) in prior text / [changeStart, newEnd) in post text
 * is GUARANTEED UNCHANGED.  No text re-verification is required for those
 * regions.
 *
 * Three cases (paragraph-local character offsets):
 *
 *   Before  — sentence ends at or before changeStart.
 *             Guaranteed unchanged.  Match old sentence at same local start
 *             and restore wholesale.
 *
 *   After   — sentence starts at or after newEnd.
 *             Guaranteed unchanged; position shifted by delta.  Match old
 *             sentence at (new start − delta) and restore wholesale.
 *
 *   Dirty   — sentence overlaps the change range.
 *             Match old dirty sentences to new dirty sentences IN ORDINAL ORDER
 *             (1st old dirty → 1st new dirty, etc.).  Position-based matching
 *             breaks when an edit shifts sentence starts within the dirty region;
 *             text-equality matching is wrong because duplicate sentence text is
 *             common (S1 ≠ S3 even if text(S1) = text(S3)).
 *             For each matched pair: token-level prefix/suffix preservation.
 *             Unmatched new sentences (split) stay fully new.
 *             Unmatched old sentences (merge) are discarded.
 *
 *   Split   — adding . ? ! or any other sentence-boundary character causes the
 *             containing sentence to split into two.  The first new sentence is
 *             dirty (overlaps the change range) and is handled above.  The SECOND
 *             new sentence starts AFTER newEnd, so it falls into the "after" zone.
 *             The "after" wholesale lookup (old sentence at nsLocalStart − delta)
 *             finds nothing because no old sentence ever started there.  In this
 *             case we fall back to a token-level position lookup against the
 *             paragraph-wide old-token map.  Every token whose paragraph-local
 *             position maps back (via − delta) to a known old token gets its state
 *             restored; tokens at or around the split point that have no old
 *             counterpart stay new.
 *
 * Pure function — returns a new array.
 */
function preserveSentenceState(old: sentenceDTO[], next: sentenceDTO[], range: ChangeRange): sentenceDTO[] {
  const { changeStart, changeEnd, newEnd } = range;
  const delta: number = newEnd - changeEnd; // post.length - prior.length

  const result: sentenceDTO[] = next.map((s) => ({ ...s, tokens: [...s.tokens] }));

  // Index old sentences by paragraph-local start for before/after lookups.
  // sentence.index is paragraph-local (char offset within paragraph text).
  const oldByLocalStart: Map<number, sentenceDTO> = new Map<number, sentenceDTO>(old.map((s) => [s.index, s]));

  // Paragraph-wide old-token map keyed by paragraph-local character position.
  // Used as a fallback when a sentence falls into the "after" zone but has no
  // matching old sentence — the canonical case is a sentence split where the
  // second new sentence never existed as a standalone sentence in old state.
  const oldTokenByLocalPos: Map<number, tokenDTO> = new Map<number, tokenDTO>();
  for (const os of old) {
    for (const ot of os.tokens) {
      oldTokenByLocalPos.set(os.index + ot.index, ot);
    }
  }

  // Collect old dirty sentences in ordinal order for dirty-region matching.
  // A sentence is dirty if it overlaps [changeStart, changeEnd) in prior text.
  //
  // The end condition uses >= (not >) so that a sentence whose last character
  // falls exactly AT changeStart is included.  This is the "pure append" case:
  // the user typed at the very end of the sentence, so computeChangeRange gives
  // changeStart = changeEnd = old sentence length.  With strict >, the old
  // sentence (localEnd === changeStart) was excluded from oldDirty, while the
  // new sentence (localEnd > changeStart) was classified as dirty.  The mismatch
  // left the new sentence with no matching old dirty sentence → all tokens
  // remained editState.new → buildLocalParagraph sent every token through the
  // trie → contextual words were downgraded to correct → blue highlights dropped
  // on every keystroke until the server re-assessed.
  const oldDirty: sentenceDTO[] = old.filter((s) => {
    const localEnd: number = s.index + s.length;
    return localEnd >= changeStart && s.index < changeEnd;
  });

  let dirtyOrdinal: number = 0;
  const consumedByZone = new Set<number>(); // old sentence indices consumed by BEFORE/AFTER lookups

  // Format a sentence for logging: shows the dirty region inline as [old|new].
  // Uses changeStart/changeEnd/newEnd (already computed, in closure scope) to slice.
  const fmtSent = (old: sentenceDTO | undefined, neu: sentenceDTO): string => {
    if (!old) return `'${neu.input}'`;
    const oS: number = Math.max(0, changeStart - old.index);
    const oE: number = Math.min(old.input.length, Math.max(oS, changeEnd - old.index));
    const nS: number = Math.max(0, changeStart - neu.index);
    const nE: number = Math.min(neu.input.length, Math.max(nS, newEnd - neu.index));
    const oc: string = old.input.slice(oS, oE);
    const nc: string = neu.input.slice(nS, nE);
    if (oc === nc) return `'${neu.input}'`;
    return `'${neu.input.slice(0, nS)}[${oc}|${nc}]${neu.input.slice(nE)}'`;
  };

  for (let i: number = 0; i < next.length; i++) {
    const ns: sentenceDTO = next[i];
    const nsLocalStart: number = ns.index;
    const nsLocalEnd: number = nsLocalStart + ns.length;
    let zone: string;
    let oldSent: sentenceDTO | undefined;

    if (nsLocalEnd <= changeStart) {
      // ── Before dirty region ────────────────────────────────────────────────
      // LCP/LCS guarantees text is unchanged — restore state from the old sentence.
      // Preserve seqId so mergeResult() can match server responses sentence-by-sentence:
      // unchanged sentences keep their old seqId; only dirty sentences get the new paragraph seqId.
      //
      // Use preserveTokenState rather than `tokens: oldSent.tokens` wholesale.
      // The edge case is a pure deletion from the end of a paragraph: the new
      // sentence ends exactly at changeStart (nsLocalEnd === changeStart) and is
      // SHORTER than the old sentence.  Wholesale restore would copy old tokens
      // whose positions are beyond the new sentence's length, producing stale
      // out-of-bounds decorations on screen (the well-documented H5/H5b bug).
      // preserveTokenState scans fresh ↔ old by input text and only restores state
      // for tokens that exist in the fresh set — out-of-bounds old tokens are
      // naturally excluded because they have no fresh counterpart.
      oldSent = oldByLocalStart.get(nsLocalStart);
      if (oldSent) {
        consumedByZone.add(oldSent.index);
        const mergedTokens: tokenDTO[] = preserveTokenState(oldSent.tokens, result[i].tokens);
        if (oldSent.length === result[i].length) {
          zone = 'BEFORE';
          result[i] = {
            ...result[i],
            seqId: oldSent.seqId,
            editState: oldSent.editState,
            suggestion: oldSent.suggestion,
            tokens: mergedTokens,
          };
        } else {
          zone = 'BEFORE(trunc)';
          result[i] = {
            ...result[i],
            tokens: mergedTokens,
            editState: maxEditState(deriveEditStateFromTokens(mergedTokens), editStateEnum.new),
          };
        }
      } else {
        zone = 'BEFORE(nomatch)';
      }
    } else if (nsLocalStart >= newEnd) {
      // ── After dirty region ─────────────────────────────────────────────────
      // LCP/LCS guarantees text is unchanged — restore wholesale, no re-verify.
      // Token positions are sentence-local, unaffected by paragraph-level shift.
      // Preserve seqId for the same reason as the BEFORE zone above.
      oldSent = oldByLocalStart.get(nsLocalStart - delta);
      if (oldSent) {
        consumedByZone.add(oldSent.index);
        zone = 'AFTER';
        result[i] = {
          ...result[i],
          seqId: oldSent.seqId,
          editState: oldSent.editState,
          suggestion: oldSent.suggestion,
          tokens: oldSent.tokens,
        };
      } else {
        zone = 'AFTER(split)';
        // ── Split fallback ──────────────────────────────────────────────────
        // No old sentence started at (nsLocalStart − delta): this sentence was
        // carved out of an old dirty sentence by a split (. ? ! etc.).
        // Restore each token individually using the paragraph-wide token map.
        // Tokens with a matching old position get their state back; any token
        // at or around the split point that has no old counterpart stays new.
        //
        // Force at least editState.local even if all tokens restored as checked.
        // Punctuation (.?!) is not tokenised, so a sentence-boundary-only edit
        // (e.g. inserting '.') produces no new tokens — without this floor the
        // split sentence would emerge as 'checked' and never be re-dispatched to
        // the server for contextual re-assessment.
        const mergedTokens: tokenDTO[] = result[i].tokens.map((nt) => {
          const ntParaLocalPos: number = nsLocalStart + nt.index;
          const oldParaLocalPos: number = ntParaLocalPos - delta;
          const ot: tokenDTO | undefined = oldTokenByLocalPos.get(oldParaLocalPos);
          return ot
            ? { ...nt, editState: ot.editState, spellingState: ot.spellingState, suggestions: ot.suggestions }
            : nt;
        });
        result[i] = {
          ...result[i],
          tokens: mergedTokens,
          editState: maxEditState(deriveEditStateFromTokens(mergedTokens), editStateEnum.new),
        };
      }
    } else {
      // ── Overlaps dirty region ──────────────────────────────────────────────
      // Match by ordinal among dirty sentences — not by position or text content.
      //
      // Force at least editState.new regardless of token states.
      // Punctuation (.?!) is not tokenised, so a sentence-boundary-only edit
      // (e.g. inserting '.') produces no new tokens — without this floor the
      // dirty sentence would emerge as 'checked' and never be re-dispatched to
      // the server for contextual re-assessment.
      // Using `new` (not `local`) matches the split-fallback zone above and
      // gives `local` a single unambiguous stored meaning: trie-done, server-pending.
      oldSent = oldDirty[dirtyOrdinal];
      dirtyOrdinal++;
      if (oldSent) {
        zone = 'DIRTY';
        const mergedTokens: tokenDTO[] = preserveTokenState(oldSent.tokens, result[i].tokens);
        result[i] = {
          ...result[i],
          tokens: mergedTokens,
          editState: maxEditState(deriveEditStateFromTokens(mergedTokens), editStateEnum.new),
          suggestion: oldSent.suggestion,
        };
      } else {
        zone = 'DIRTY(new)';
        // No old match → unmatched new sentence (split) stays fully new.
      }
    }

    logger.log(`s${i} ${zone!.padEnd(13)} ${fmtSent(oldSent, result[i])} [${result[i].editState}]`);
  }

  // Log old dirty sentences that had no new counterpart — entire sentence deleted.
  // oldDirty.slice(dirtyOrdinal) IS the unmatched set from the processing loop above.
  // Exclude sentences consumed by BEFORE/AFTER lookups — they appear in oldDirty due
  // to the >= changeStart boundary but were not deleted.
  for (let di = dirtyOrdinal; di < oldDirty.length; di++) {
    if (!consumedByZone.has(oldDirty[di].index)) {
      logger.log(`s? DELETED        '${oldDirty[di].input}'`);
    }
  }

  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Paragraph level
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Merge old paragraph state into a freshly re-tokenised paragraph.
 *
 * Computes the precise change range from the two input strings, then delegates
 * to sentence-level and (for dirty sentences) token-level preservation.
 *
 * Paragraph editState is derived bottom-up from the merged sentences:
 *   new > local > checked.
 *
 * Pure function — returns a new paragraphDTO.
 */
export function preserveParagraphState(old: paragraphDTO, next: paragraphDTO): paragraphDTO {
  const range = computeChangeRange(old.input, next.input);
  const mergedSentences = preserveSentenceState(old.sentences, next.sentences, range);

  return {
    ...next,
    editState: deriveEditStateFromSentences(mergedSentences),
    sentences: mergedSentences,
  };
}
